import { FusionAuthProvider } from "@fusionauth/react-sdk";
import { CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/system";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { defaults } from "chart.js";
import DOMPurify from "dompurify";
import React, { useReducer } from "react";
import { BrowserRouter } from "react-router";
import AppRouter from "./AppRouter.tsx";
import {
  defaultNavBarConfig,
  NavBarContext,
  NavBarDispatchContext,
  navBarReducer,
} from "./contexts/NavBarContext.tsx";
import {
  ProjectContext,
  ProjectDispatchContext,
  projectReducer,
} from "./contexts/ProjectContext.tsx";
import { EmptyProject } from "./models/Project.ts";
import theme from "./styles/theme";
import { setLicenseKey } from "./utils/main.ts";

defaults.font.family = "'DM Sans',Arial, sans-serif";

setLicenseKey();

function App() {
  const fusionAuthConfig = {
    clientId:
      import.meta.env.VITE_AUTH_CLIENT_ID || window._env_?.AUTH_CLIENT_ID || "",
    serverUrl:
      import.meta.env.VITE_AUTH_SERVER_URL ||
      window._env_?.AUTH_SERVER_URL ||
      "",
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin + "/login",
    shouldAutoRefresh: true,
    shouldAutoFetchUserInfo: true,
    autoRefreshSecondsBeforeExpiry: 30,
  };

  const [project, dispatch] = useReducer(projectReducer, EmptyProject);
  const [navbar, navbarDispatch] = useReducer(
    navBarReducer,
    defaultNavBarConfig,
  );

  if (window.trustedTypes?.createPolicy && !window.trustedTypes.defaultPolicy) {
    window.trustedTypes.createPolicy("default", {
      createHTML: (input) =>
        DOMPurify.sanitize(input, {
          RETURN_TRUSTED_TYPE: true,
        }) as unknown as string,
      createScriptURL: (input) => input,
    });
  }

  return (
    <div className={"app"}>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <FusionAuthProvider {...fusionAuthConfig}>
            <NavBarContext.Provider value={navbar}>
              <NavBarDispatchContext.Provider value={navbarDispatch}>
                <ProjectContext.Provider value={project}>
                  <ProjectDispatchContext.Provider value={dispatch}>
                    <BrowserRouter>
                      <CssBaseline />
                      <AppRouter />
                    </BrowserRouter>
                  </ProjectDispatchContext.Provider>
                </ProjectContext.Provider>
              </NavBarDispatchContext.Provider>
            </NavBarContext.Provider>
          </FusionAuthProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
